// Leadership "China requests" dashboard (#1589). Mounts under
// `/dashboards/china-requests` behind `dashboard.read`; the route gate
// (`PermissionGate`) is authoritative, so this component does NOT
// re-check the permission. READ-ONLY: it only renders the #1588 summary
// (`GET /api/dashboards/china-requests/summary`) — no mutations, no edit
// controls. Structure mirrors the admin-only `RevenueDashboardPage`
// header + query-branch shell.
import { useTranslation } from 'react-i18next';
import type { ChinaRequestsDashboardSummary, ChinaRequestsSummaryQuery } from '@magimp/shared';
import { EmptyState, ErrorState, LoadingState } from '../../../ui/index.js';
import { useLocale } from '../../../i18n/useLocale.js';
import { formatDateTime } from '../../../i18n/formatters.js';
import { useChinaRequestsDashboard } from './queries.js';
import { usePeriodQuery } from './usePeriodQuery.js';
import { PeriodSelector } from './PeriodSelector.js';
import { KpiRow } from './KpiRow.js';
import { SlaRow } from './SlaRow.js';
import { StatusFunnel } from './StatusFunnel.js';
import { TrendChart } from './TrendChart.js';
import { ManagersTable } from './ManagersTable.js';
import { RefusalReasons } from './RefusalReasons.js';
import { SECTION_GAP, type DashboardT } from './section.js';

const MSK_TIME_ZONE = 'Europe/Moscow';

export function ChinaRequestsDashboardPage() {
  const { t } = useTranslation(['dashboards', 'requests', 'common']);
  const period = usePeriodQuery();
  const query = useChinaRequestsDashboard(period.query);

  return (
    <div
      data-testid="china-requests-dashboard-page"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: SECTION_GAP,
        minHeight: 0,
        flex: 1,
        padding: 8,
      }}
    >
      <header style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <h1 style={{ fontSize: 20, fontWeight: 600, margin: 0, flex: '1 1 auto' }}>
          {t('dashboards:chinaRequests.title')}
        </h1>
      </header>

      <PeriodSelector state={period} t={t} />

      {period.query === null ? (
        <EmptyState text={t('dashboards:chinaRequests.customPrompt')} />
      ) : query.isPending ? (
        <LoadingState />
      ) : query.isError ? (
        <ErrorState
          title={t('dashboards:chinaRequests.error')}
          onRetry={() => void query.refetch()}
          retryLabel={t('dashboards:chinaRequests.retry')}
        />
      ) : (
        <DashboardContent data={query.data} t={t} />
      )}
    </div>
  );
}

function DashboardContent({ data, t }: { data: ChinaRequestsDashboardSummary; t: DashboardT }) {
  const { locale } = useLocale();
  // WHY: drive the trend off the summary's already-resolved [from, to) so both
  // endpoints aggregate the identical window — eliminating the two-new Date()
  // preset drift that occurs when the summary and the chart each call new Date()
  // independently on a rolling preset. data.period.from/to are ISO-8601 Z
  // instants that satisfy the shared query schema's .datetime({ offset: true })
  // and from < to.
  const trendQuery: ChinaRequestsSummaryQuery = {
    period: 'custom',
    from: data.period.from,
    to: data.period.to,
  };
  return (
    <>
      <div style={{ fontSize: 12, color: 'var(--fg-2)' }}>
        {data.period.label}
        {' · '}
        {t('dashboards:chinaRequests.computedAt', {
          time: formatDateTime(data.computedAt, locale, { timeZone: MSK_TIME_ZONE }),
        })}
      </div>
      <KpiRow kpis={data.kpis} locale={locale} t={t} />
      <SlaRow sla={data.sla} locale={locale} t={t} />
      <StatusFunnel funnel={data.funnel} t={t} />
      <TrendChart query={trendQuery} locale={locale} t={t} />
      <ManagersTable managers={data.managers} locale={locale} t={t} />
      <RefusalReasons refusalReasons={data.refusalReasons} t={t} />
    </>
  );
}
